import { useEffect, useState, useRef, useCallback } from "react";

/**
 * Optimized parallax hook with performance enhancements:
 * - RAF-based updates for smooth 60fps animations
 * - Throttling to prevent excessive recalculations
 * - Intersection Observer to only run when visible
 * - GPU-accelerated transforms
 * - SSR-safe with proper hydration handling
 * @param factor - Parallax factor (default: 0.2)
 * @param minWidth - Minimum window width for parallax (default: 640)
 * @param elementRef - Optional ref to the element for intersection observing
 * @returns [offset, isDesktop, transform3d]
 */
export function useParallaxOffset(
  factor: number = 0.2, 
  minWidth: number = 640,
  elementRef?: { current: HTMLElement | null }
): [number, boolean, string] {
  const [offset, setOffset] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);
  
  const rafId = useRef<number | undefined>(undefined);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  // GPU-accelerated transform string - SSR safe
  const transform3d = isHydrated ? `translate3d(0, ${offset}px, 0)` : 'translate3d(0, 0, 0)';

  // Throttled scroll handler using RAF
  const updateParallax = useCallback(() => {
    if (!ticking.current) return;
    
    const scrollY = window.scrollY;
    if (isDesktop && isVisible && scrollY !== lastScrollY.current) {
      const newOffset = scrollY * factor;
      setOffset(newOffset);
      lastScrollY.current = scrollY;
    }
    
    ticking.current = false;
  }, [factor, isDesktop, isVisible]);

  const requestTick = useCallback(() => {
    if (!ticking.current) {
      ticking.current = true;
      rafId.current = requestAnimationFrame(updateParallax);
    }
  }, [updateParallax]);

  const handleScroll = useCallback(() => {
    requestTick();
  }, [requestTick]);

  const handleResize = useCallback(() => {
    const newIsDesktop = window.innerWidth >= minWidth;
    setIsDesktop(newIsDesktop);
    if (!newIsDesktop) {
      setOffset(0);
    } else {
      requestTick();
    }
  }, [minWidth, requestTick]);

  useEffect(() => {
    // Mark as hydrated to avoid SSR mismatch
    setIsHydrated(true);
    
    // Initial setup
    setIsDesktop(window.innerWidth >= minWidth);
    
    // Intersection Observer for visibility optimization
    let observer: IntersectionObserver | null = null;
    if (elementRef?.current) {
      observer = new IntersectionObserver(
        ([entry]) => {
          setIsVisible(entry.isIntersecting);
        },
        { 
          rootMargin: '100px 0px', // Start parallax slightly before element is visible
          threshold: 0
        }
      );
      observer.observe(elementRef.current);
    }

    // Event listeners
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    
    // Initial calculation
    requestTick();

    return () => {
      // Cleanup
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
      }
      if (observer) {
        observer.disconnect();
      }
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      ticking.current = false;
    };
  }, [factor, minWidth, elementRef, handleScroll, handleResize, requestTick]);

  return [offset, isDesktop, transform3d];
}
